import {catchError, map, switchMap} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {BehaviorSubject, from, Observable, of, Subject} from 'rxjs';
import {User} from '../../../model/user';
import {HttpService} from '../http-service.service';
import {ApiConst} from '../api-const';
import {StorageService} from '../../storage/storage.service';
import {TranslateService} from '@ngx-translate/core';
import {SearchService} from '../business/search.service';
import {ConsultCriteriaService} from '../business/consult-criteria.service';
import {AddressService} from '../business/address.service';
import {CustomTableSaverService} from '../../storage/custom-table-saver.service';
import {TreatmentChainService} from '../business/treatment-chain.service';
import {JwtHelperService} from '@auth0/angular-jwt';
import {Permissions} from '../../../model/permissions';
import {HttpHeaders, HttpResponse} from '@angular/common/http';
import {ActivatedRoute, Router} from '@angular/router';
import {ApplicationConst} from '../../../routing/routingConst';
import {AppParameterService} from '../business/app-parameter.service';
import {UserManagementService} from '../business/user-management.service';
import {User as UserOIDC, UserManager, UserManagerSettings} from "oidc-client";
import {environment} from "../../../../environments/environment";


// import { JwtHelperService } from '@auth0/angular-jwt'
/**
 * Handle all the authentication part (token validity, login)
 */
@Injectable()
export class AuthenticationService {

  /**
   * default value it's false, when the login or the auto authentication works, it's true
   */
  public isLogged: BehaviorSubject<boolean>;
  public requirePasswordChange: BehaviorSubject<boolean>;
  public documentExtData: BehaviorSubject<any>;

  /**
   * if the back-office answer with an error Message, it will be store in errorMessage
   */
  public errorMessage: Subject<string>;

  /**
   * Currently logged user
   */
  public userLogged: User;
  public userToLog: User;
  private clientID;
  /**
   *  oidc-wso2-parameters
   */
  private userManager: UserManager;
  private codeVerifier: string;

  private settings: UserManagerSettings = {
    authority: environment.wso2.wso2AuthUrl,
    client_id: environment.wso2.clientId,
    redirect_uri: environment.wso2.redirectUri,
    post_logout_redirect_uri: 'http://localhost:4200/',
    response_type: 'code',
    scope: environment.wso2.scope,
    filterProtocolClaims: true,
    loadUserInfo: true
  };

  /**
   * helper to decode jwo tokens
   */
  jwtHelper: JwtHelperService;
  remainingLabel: string;

  /**
   * instantiate variables and call the init function
   *
   * @param {HttpService} httpService - Service that will handleChangeOfSelectFold http request
   * @param {StorageService} storageService - Service that will handleChangeOfSelectFold storage
   * @param {TranslateService} translate - handle languages changes during Auth
   * @param {Router} router to do routing actions
   * @param appParameterService
   * @param userManagementService
   * @param {SearchService} searchService - handle the creation of criterias on login
   * @param {ConsultCriteriaService} consultService - handle the consult criterias (depositColumns that display search results)
   * @param {AddressService} addressService - handle finalAddress datas
   * @param {CustomTableSaverService} customTableSaverService - handle Custom Fold Datas Display (user specific)
   * @param {TreatmentChainService} treatmentChainService - handle treatments chains filling
   * @param route
   */
  constructor(private httpService: HttpService, private storageService: StorageService,
              private translate: TranslateService, private router: Router,
              private appParameterService: AppParameterService, private userManagementService: UserManagementService,
              private searchService: SearchService, private consultService: ConsultCriteriaService,
              private addressService: AddressService, private customTableSaverService: CustomTableSaverService,
              private treatmentChainService: TreatmentChainService,
              private route: ActivatedRoute) {


    this.userManager = new UserManager(this.settings);
    this.userManager.events.addUserLoaded((user: UserOIDC) => {
      console.log('User loaded', user);
    });
    this.userManager.events.addUserUnloaded(() => {
      console.log('User unloaded');
    });
    this.userLogged = new User();
    this.isLogged = new BehaviorSubject<boolean>(false);
    this.requirePasswordChange = new BehaviorSubject<boolean>(false);
    this.documentExtData = new BehaviorSubject<any>(null);
    this.errorMessage = new Subject<string>();
    this.init();
    this.jwtHelper = new JwtHelperService();
  }

  changeLang(lang: string) {
    this.translate.setDefaultLang(lang);
    this.translate.use(lang)
  }

  /**
   * check if there's a token into the local Storage and ask the back-office if it's valid.
   *  If the token is not valid, it'll be removed from everywhere
   */
  private init(): void {
    this.translate.get('LOGIN_COMPONENT.REMAINING_LOGINS_LABEL').subscribe(value => {
      this.remainingLabel = value;
    });
    const tokenInLocalStorage = this.storageService.getTokenFromStorage();
    if (tokenInLocalStorage) {
      setTimeout(() => {
        this.checkTokenValidity().subscribe(tokenIsValid => {
          if (tokenIsValid) {
            this.isLogged.next(true);
            this.requirePasswordChange.next(this.userLogged.requirePasswordChange);
            const langInStorage: string = this.storageService.getLangFromStorage();
            if (langInStorage) {
              this.changeLang(langInStorage)
            }
          } else {
            this.httpService.setToken(null);
            this.storageService.removeTokenFromStorage();
            this.storageService.removeLangFromStorage();
          }
        });
      }, 1000);
    }
    this.isLogged.subscribe(logged => {
      if (logged) {
        this.addressService.initLabelsData();
      }
    });
    this.httpService.invalidToken.subscribe(value => {
      if (value) {
        this.logout();
      }
    });
  }

  /**
   * Set a token in the HttpService if the login succeed and add it to the local storage (usefull for auto-authentication)
   * Else it put the error Message (gave by the back-office) inside errorMessage
   *
   * @param {string} userToLog - user you wants to log in
   * @param {boolean} rememberMe - if you wants to put credentials in local Storage for future connexions
   * @param {string} accessToken - if login request is from WSO, it will not hash the password because WSO2 do it by itself
   *
   * @returns {Observable<boolean>} -is equal to true if the connexion succeeded,else it's false
   */
  public login(userToLog: User, rememberMe: boolean, accessToken: string): Observable<boolean> {
    let datasToSend = new Map<string, any>();
    if (accessToken != null) {
      return this.wsoLoginRequest(userToLog, true, accessToken);
    } else {
      datasToSend.set("sCustomerID", userToLog.idCustomer);
      datasToSend.set("sUserID", userToLog.idUser);
      datasToSend.set("sUserPwd", userToLog.password);
      datasToSend.set("rememberMe", true);
      const encoder = new TextEncoder();
      const data = encoder.encode(userToLog.password);
      const hashObservable = from(crypto.subtle.digest('SHA-1', data));
      return hashObservable.pipe(
        switchMap(res => {
          let sUserPwd = btoa(String.fromCharCode.apply(null, new Uint8Array(res)));
          if (sUserPwd.endsWith('=')) {
            sUserPwd = sUserPwd.slice(0, -1);
          }
          datasToSend.set("sUserPwd", sUserPwd);

          let stringifiedDatasToSend: string = JSON.stringify(Object.fromEntries(datasToSend));
          return this.loginRequest(userToLog, rememberMe, stringifiedDatasToSend);
        })
      );
    }
  }

  private loginRequest(userToLog: User, rememberMe: boolean, datasToSend: string) {
    return this.httpService.$post(ApiConst.authenticationRequest, datasToSend).pipe(
      map((response: any) => {
        const token = response.body && response.body.datas && response.body.datas.authInfo && response.body.datas.authInfo.jwtToken;
        if (token) {
          this.storageService.removeImpersonatingUserFromStorage();
          this.storageService.removeImpersonatingTokenFromStorage();
          return this.processAuthToken(userToLog,
            token,
            null,
            rememberMe,
            response.body.datas.authInfo.sLangID,
            response.body.datas.authInfo.requirePasswordChange,
            new Permissions(response.body.datas.permissions));
        } else {
          let errorMsg = response && response.body && response.body.sMsgError;
          if (errorMsg) {
            let remainingLoginTrial = 5;
            if (response.body.datas && response.body.datas.remainingLoginTrial) {
              remainingLoginTrial = this.parseStringAsNumber(response.body.datas.remainingLoginTrial);
              errorMsg = errorMsg + ' ' + this.remainingLabel + ': ' + remainingLoginTrial;
            }
            this.errorMessage.next(errorMsg);
          }
          return false;
        }
      })
    );
  }

  private wsoLoginRequest(userToLog: User, rememberMe: boolean, wsoToken: string) {
    const datasToSend = {"wsoAccessToken": wsoToken}
    return this.httpService.$post(ApiConst.wsoAuthenticationRequest, datasToSend).pipe(
      map((response: any) => {
        const token = response.body && response.body.datas && response.body.datas.authInfo && response.body.datas.authInfo.jwtToken;
        if (token) {
          this.storageService.removeImpersonatingUserFromStorage();
          this.storageService.removeImpersonatingTokenFromStorage();
          return this.processAuthToken(userToLog,
            token,
            wsoToken,
            rememberMe,
            response.body.datas.authInfo.sLangID,
            response.body.datas.authInfo.requirePasswordChange,
            new Permissions(response.body.datas.permissions));
        } else {
          let errorMsg = response && response.body && response.body.sMsgError;
          if (errorMsg) {
            let remainingLoginTrial = 5;
            if (response.body.datas && response.body.datas.remainingLoginTrial) {
              remainingLoginTrial = this.parseStringAsNumber(response.body.datas.remainingLoginTrial);
              errorMsg = errorMsg + ' ' + this.remainingLabel + ': ' + remainingLoginTrial;
            }
            this.errorMessage.next(errorMsg);
          }
          return false;
        }
      })
    );
  }

  public processAuthToken(userToLog: User, token: string, wsoToken: string, rememberMe: boolean, lang: string,
                          requirePasswordChange: boolean, permissions: Permissions) {
    if (token) {
      // ------------------ real Auth Part ------------------ //
      this.httpService.setToken(token);
      this.httpService.setWsoToken(wsoToken);
      this.storageService.addTokenInStorage(token);
      this.getAppParameters();
      const decodedToken = this.jwtHelper.decodeToken(token);
      userToLog.firstName = decodedToken.firstName;
      userToLog.groupId = decodedToken.sUserGroup;
      userToLog.isAuthorizedExpress = 'true' === decodedToken.isAuthorizedExpress;
      userToLog.isAuthorizedSendMail = 'true' === decodedToken.isAuthorizedSendMail;
      userToLog.isErlAuthorized = 'true' === decodedToken.isErlAuthorized;
      userToLog.lastName = decodedToken.lastName;
      userToLog.lastConnection = decodedToken.sUserLastConnection;
      userToLog.businessCategory = decodedToken.businessCategory;
      userToLog.physicalDeliveryOfficeName = decodedToken.physicalDeliveryOfficeName;
      userToLog.supervisor = 'true' === decodedToken.bSupervisor;
      userToLog.admin = 'true' === decodedToken.bAdmin;
      userToLog.requirePasswordChange = 'true' === decodedToken.requirePasswordChange;
      userToLog.permissions = permissions;
      if (requirePasswordChange) {
        this.router.navigateByUrl(ApplicationConst.resetPasswordPath).then(done => {
          if (done) {
            console.log();
          }
        });
      }
      if (rememberMe) {
        this.storageService.addUserInStorage(userToLog);
      }
      delete userToLog.password;
      this.storageService.setCurrentUserInStorage(userToLog);
      this.userLogged.idCustomer = userToLog.idCustomer;
      this.userLogged.idUser = userToLog.idUser;
      this.userLogged.groupId = userToLog.groupId;
      this.userLogged.businessCategory = userToLog.businessCategory;
      this.userLogged.physicalDeliveryOfficeName = userToLog.physicalDeliveryOfficeName;
      this.userLogged.requirePasswordChange = userToLog.requirePasswordChange;
      this.customTableSaverService.userLogged = userToLog;
      const userLang = lang ? lang.substr(0, 2).toLowerCase() : 'fr';
      this.storageService.addLangInStorage(userLang);
      this.changeLang(userLang)
      this.isLogged.next(true);
      this.requirePasswordChange.next(this.userLogged.requirePasswordChange);
      return true;
    }
    return false;
  }

  public parseStringAsNumber(x) {
    const parsed = parseInt(x, 10);
    if (isNaN(parsed)) {
      return 0;
    }
    return parsed;
  }

  getAppParameters() {
    this.httpService.$post(ApiConst.appParameters, '').subscribe(response => {
      // -------------------- Criterias -------------------- //
      console.log("criteria", response.body.datas.objCriteriaNameList.listObjDataDef, ApiConst.appParameters);
      this.searchService.buildCriterias(response.body.datas.objCriteriaNameList.listObjDataDef);
      this.consultService.buildCriterias(response.body.datas.objCriteriaNameList.listObjDataDesc);
      // ---------------- End of Criterias ---------------- //

      this.appParameterService.buildEnvelopes(response.body.datas.listObjEnvelope);
      this.appParameterService.buildPostages(response.body.datas.listObjPostage);
      // -------------------- Adresses -------------------- //
      this.addressService.buildAddressDatas(response.body.datas.countryISOPostal);
      // ----------------- End of Adresses ---------------- //

      // -------------------- Treatment chain -------------------- //
      this.treatmentChainService.buildTreatmentChains(response.body.datas.listObjTrtChainGlobal);

      // ----------------- End of Treatment chains ---------------- //

      // -------------------- Accepted files -------------------- //
      // this.kindOfAcceptedFilesService.buildExtensionsLists(response.json().datas.appParameters.extensionsDocuments);
      this.documentExtData.next(response.body.datas.extensionsDocuments);
    });
  }

  /**
   * remove the token from the httpService and the local storage.
   */
  public logout(): void {
    this.doLogout();
    //            /_!_\ Not implemented yet /_!_\
    // this.customTableSaverService.updateUserParamsToServer();
  }

  public doLogout(): void {
    if(localStorage.getItem('client')==='mbfs' || localStorage.getItem('client')==='metc' || localStorage.getItem('client')==='brc'){
      this.router.navigate(['/logout']);
      this.isLogged.next(false);
      this.requirePasswordChange.next(false);
      this.httpService.setToken(null);
      localStorage.setItem('wsoAccessToken', '');
      localStorage.setItem('wsoIdToken', '');
      this.requirePasswordChange.next(false);
      this.httpService.setToken(null);
      this.storageService.clearStorage();
    }
    else{
      if (!localStorage.hasOwnProperty('hasBeenWsoLoggedIn') || !localStorage.hasOwnProperty('wsoAccessToken') || !localStorage.hasOwnProperty('wsoIdToken') || (localStorage.getItem('hasBeenWsoLoggedIn') === 'false' && localStorage.getItem('wsoAccessToken').length == 0 && localStorage.getItem('wsoIdToken').length == 0)) {
        this.isLogged.next(false);
        this.requirePasswordChange.next(false);
        this.httpService.setToken(null);
        this.storageService.clearStorage();
      } else {
        this.logoutWso2();
      }
    }



  }

  public terminateImpersonation(): Observable<boolean> {
    /* const oldToken = this.storageService.getImpersonatingTokenFromStorage();
     const user = this.storageService.getImpersonatingUserFromStorage();
     const lang = this.storageService.getLangFromStorage();*/
    this.doLogout();
    this.storageService.removeImpersonatingUserFromStorage();
    this.storageService.removeImpersonatingTokenFromStorage();
    window.location.reload();
    /*this.storageService.addTokenInStorage(oldToken);
    this.storageService.setCurrentUserInStorage(user);*/
    /*this.processAuthToken(user,
      oldToken,
      false,
      lang,
      false,
      new Permissions(user.permissions));*/
    return of(true);
  }

  public impersonate(userToLog: User): Observable<boolean> {
    return this.httpService.$post(ApiConst.impersonationRequest, {userId: userToLog.idUser}).pipe(
      map((response: any) => {
        const token = response.body && response.body.datas && response.body.datas.authInfo && response.body.datas.authInfo.jwtToken;
        if (token) {
          const oldToken = this.storageService.getTokenFromStorage();
          const oldUser = this.storageService.getCurrentUserInStorage();
          this.doLogout();
          this.processAuthToken(userToLog,
            token,
            null,
            false,
            response.body.datas.authInfo.sLangID,
            response.body.datas.authInfo.requirePasswordChange,
            new Permissions(response.body.datas.permissions));
          this.storageService.addImpersonatingTokenFromStorage(oldToken);
          this.storageService.addImpersonatingUserFromStorage(oldUser);
          return true;
        } else {
          let errorMsg = response && response.body && response.body.sMsgError;
          if (errorMsg) {
            let remainingLoginTrial = 5;
            if (response.body.datas && response.body.datas.remainingLoginTrial) {
              remainingLoginTrial = this.parseStringAsNumber(response.body.datas.remainingLoginTrial);
              errorMsg = errorMsg + ' ' + this.remainingLabel + ': ' + remainingLoginTrial;
            }
            this.errorMessage.next(errorMsg);
          }
        }
        return false;
      }));
  }

  /*
  * get current user
   */
  public getCurrentUser(): User {
    return this.storageService.getCurrentUserInStorage();
  }

  public isAdminUser(): boolean {
    const user = this.storageService.getCurrentUserInStorage();
    if (user) {
      const bc = user.businessCategory;
      return bc && bc !== '' && (bc.indexOf('adminUsers') >= 0);
    }
    return false;
  }

  public isAdminErl(): boolean {
    const user = this.storageService.getCurrentUserInStorage();
    if (user) {
      const bc = user.businessCategory;
      return bc && bc !== '' && (bc.indexOf('adminErl') >= 0);
    }
    return false;
  }

  public isAdminEmail(): boolean {
    const user = this.storageService.getCurrentUserInStorage();
    if (user) {
      const bc = user.businessCategory;
      return bc && bc !== '' && (bc.indexOf('adminEmail') >= 0);
    }
    return false;
  }

  public isAdminExpress(): boolean {
    const user = this.storageService.getCurrentUserInStorage();
    if (user) {
      const bc = user.businessCategory;
      return bc && bc !== '' && (bc.indexOf('adminExpress') >= 0);
    }
    return false;
  }

  public isAdminLog(): boolean {
    const user = this.storageService.getCurrentUserInStorage();
    if (user) {
      const bc = user.businessCategory;
      return bc && bc !== '' && (bc.indexOf('adminLog') >= 0);
    }
    return false;
  }

  public isAdminNumen(): boolean {
    const user = this.storageService.getCurrentUserInStorage();
    if (user && user.idUser) {
      return 'ADMINNUMEN' === user.idUser.toUpperCase();
    }
    return false;
  }

  public isImpersonnated(): boolean {
    const token = this.storageService.getImpersonatingTokenFromStorage();
    return (token && token !== '');
  }

  /**
   * check if a token is valid (asking back-office)
   *
   * @returns {Observable<boolean>} - true if it's valid, else false
   */
  public checkTokenValidity(): Observable<boolean> {
    const tokenInLocalStorage = this.storageService.getTokenFromStorage();
    this.httpService.setToken(tokenInLocalStorage);
    return this.httpService.$post(ApiConst.checkAuthTokenRequest, '').pipe(
      map((response: HttpResponse<any>) => {
        if (response.body && response.body.bTokenOk) {
          const userToLog = new User();
          const decodedToken = this.jwtHelper.decodeToken(tokenInLocalStorage);
          userToLog.firstName = decodedToken.firstName;
          userToLog.lastName = decodedToken.lastName;
          userToLog.lastConnection = decodedToken.sUserLastConnection;
          userToLog.businessCategory = decodedToken.businessCategory;
          userToLog.supervisor = 'true' === decodedToken.bSupervisor;
          userToLog.admin = 'true' === decodedToken.bAdmin;
          userToLog.requirePasswordChange = 'true' === decodedToken.requirePasswordChange;
          userToLog.permissions = new Permissions(response.body.datas.permissions);
          this.userLogged = userToLog;
          this.customTableSaverService.userLogged = userToLog;
        } else {
        }
        // /_!_\ Temporary: Pending back-office Fold Datatables gestion /_!_\
        return !!(!!response.body && response.body.bTokenOk);
      }));
  }

  async redirectToWso2(): Promise<void> {
    this.codeVerifier = this.generateCodeVerifier();
    sessionStorage.setItem('codeVerifier', this.codeVerifier);
    const codeChallenge = await this.generateCodeChallenge(this.codeVerifier);
    let client = localStorage.getItem('client');
    let clientID = null;
    if(client != null && client != "null") {
      if(client == 'mbfs'){
        clientID = environment.wso2.clientIdMb;
      }
      else if (client == 'metc'){
        clientID = environment.wso2.clientIdMetc;
      }
      else if (client == 'brc'){
        clientID = environment.wso2.clientIdBrc;
      }
    }
    else{
      clientID = environment.wso2.clientId;
    }
    sessionStorage.setItem('clientID', clientID);
    window.location.href = `${environment.wso2.wso2AuthUrl}?response_type=code&client_id=${clientID}&redirect_uri=${encodeURIComponent(environment.wso2.redirectUri)}&scope=${encodeURIComponent(environment.wso2.scope)}&code_challenge=${codeChallenge}&code_challenge_method=S256`;
  }

  generateCodeVerifier(length: number = 128): string {
    const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    let codeVer = '';
    for (let i = 0; i < length; i++) {
      codeVer += possible.charAt(Math.floor(Math.random() * possible.length));
    }
    return codeVer;
  }

  async generateCodeChallenge(verifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    return btoa(String.fromCharCode.apply(null, new Uint8Array(digest)))
      .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  handleAuthCallback(): Observable<boolean> {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (code) {
      return this.exchangeCodeForToken(code);
    } else {
      return of(false);
    }
  }

  exchangeCodeForToken(code: string): Observable<boolean> {
    const clientID = sessionStorage.getItem('clientID');
    this.codeVerifier = sessionStorage.getItem('codeVerifier') || '';
    const body = new URLSearchParams();
    body.set('grant_type', 'authorization_code');
    body.set('code', code);
    body.set('redirect_uri', environment.wso2.redirectUri);
    body.set('client_id', clientID);
    body.set('code_verifier', this.codeVerifier);

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });
    return this.httpService.post(environment.wso2.tokenEndpoint, body.toString(), {headers}).pipe(
      switchMap((response: any) => {
        const idToken = response.id_token;
        const accessToken = response.access_token;
        const decodedToken = this.jwtHelper.decodeToken(idToken);
        const ldapUser = decodedToken.LDAP_user;
        const codeClient = decodedToken.CodeClient;

        localStorage.setItem('wsoAccessToken', accessToken);
        localStorage.setItem('wsoIdToken', idToken);

        this.userToLog = new User();
        this.userToLog.idCustomer = codeClient;
        this.userToLog.idUser = ldapUser;

        return this.login(this.userToLog, true, accessToken);
      }),
      map(success => {
        if (success) {
          return true;
        } else {
          return false;
        }
      }),
      catchError((error: any) => {
        console.error('Error exchanging code for token', error);
        return of(false);
      })
    );
  }

  logoutWso2() {
    const wsoIdToken = localStorage.getItem('wsoIdToken');
    localStorage.setItem('wsoAccessToken', '');
    localStorage.setItem('wsoIdToken', '');
    this.requirePasswordChange.next(false);
    this.httpService.setToken(null);
    this.storageService.clearStorage();
    window.location.href = environment.wso2.logoutEndpoint + `?id_token_hint=${wsoIdToken}&post_logout_redirect_uri=${environment.wso2.post_logout_redirect_uri}`;
  }

  isWsoRegistered(username: string, customerGroup: string): Observable<boolean> {
    const params = {username: username, customerGroup: customerGroup};

    return this.httpService.$post(ApiConst.isWsoRegistered, params).pipe(
      map((response: HttpResponse<any>) => {
        if (response.body && response.body.datas) {
          return response.body.datas.userExists
        }
        return false;
      })
    );
  }
}
